<script lang="ts">
  import { selectedPerson, selectedPersonId, currentPeople, currentRels,
           updatePerson, deletePerson, linkParent, linkSpouse, unlink,
           createPerson, currentTreeId } from '../lib/stores';
  import { isLiving } from '../lib/living';
  import type { Person } from '../lib/db';

  let draft: Person | null = null;
  let linkMode: 'parent' | 'child' | 'spouse' | null = null;
  let pickTarget = '';

  $: if ($selectedPerson && (!draft || draft.id !== $selectedPerson.id)) {
    draft = { ...$selectedPerson };
    linkMode = null;
    pickTarget = '';
  }
  $: if (!$selectedPerson) { draft = null; }

  async function save() {
    if (!draft) return;
    await updatePerson(draft.id, {
      name: draft.name, label: draft.label, link: draft.link,
      gender: draft.gender, dob: draft.dob, death: draft.death,
      birthPlace: draft.birthPlace, deathPlace: draft.deathPlace,
      notes: draft.notes,
      livingOverride: draft.livingOverride, living: draft.living,
      publishAuthorized: draft.publishAuthorized,
    });
  }

  async function remove() {
    if (!draft) return;
    if (!confirm(`Delete ${draft.name}? All relationships are removed.`)) return;
    await deletePerson(draft.id);
    selectedPersonId.set(null);
  }

  async function addLink() {
    if (!draft || !pickTarget) return;
    if (linkMode === 'parent') await linkParent(pickTarget, draft.id);
    if (linkMode === 'child')  await linkParent(draft.id, pickTarget);
    if (linkMode === 'spouse') await linkSpouse(draft.id, pickTarget);
    linkMode = null; pickTarget = '';
  }

  async function quickAddRelative(kind: 'parent' | 'child' | 'spouse') {
    if (!draft || !$currentTreeId) return;
    const np = await createPerson($currentTreeId, { name: 'New person' });
    if (kind === 'parent') await linkParent(np.id, draft.id);
    if (kind === 'child')  await linkParent(draft.id, np.id);
    if (kind === 'spouse') await linkSpouse(draft.id, np.id);
    selectedPersonId.set(np.id);
  }

  $: relsOnPerson = draft
    ? $currentRels.filter((r) => r.a === draft!.id || r.b === draft!.id)
    : [];

  $: peopleById = new Map($currentPeople.map((p) => [p.id, p]));

  $: candidates = draft
    ? $currentPeople.filter((p) => p.id !== draft!.id)
    : [];
</script>

{#if draft}
  <aside class="drawer" role="dialog" aria-label="Edit person">
    <header>
      <div class="stack-tight">
        <h3>Edit person</h3>
        <span class="chip">id · {draft.id.slice(0, 8)}</span>
      </div>
      <button class="ghost small" on:click={() => selectedPersonId.set(null)} aria-label="Close">✕</button>
    </header>

    <div class="stack">
      <div class="field">
        <label for="f-name">name</label>
        <input id="f-name" bind:value={draft.name} on:blur={save} />
      </div>
      <div class="field">
        <label for="f-label">label · short display</label>
        <input id="f-label" bind:value={draft.label} on:blur={save} />
      </div>
      <div class="row">
        <div class="field" style="flex:1">
          <label for="f-gender">gender</label>
          <select id="f-gender" bind:value={draft.gender} on:change={save}>
            <option value="unknown">unknown</option>
            <option value="female">female</option>
            <option value="male">male</option>
            <option value="other">other</option>
          </select>
        </div>
        <div class="field" style="flex:1">
          <label for="f-link">source</label>
          <input id="f-link" bind:value={draft.link} on:blur={save} placeholder="https://…" />
        </div>
      </div>
      <div class="row">
        <div class="field" style="flex:1">
          <label for="f-dob">born · YYYY-MM-DD</label>
          <input id="f-dob" bind:value={draft.dob} on:blur={save} placeholder="1952-03-14" />
        </div>
        <div class="field" style="flex:1">
          <label for="f-death">died · YYYY-MM-DD</label>
          <input id="f-death" bind:value={draft.death} on:blur={save} placeholder="—" />
        </div>
      </div>
      <div class="row">
        <div class="field" style="flex:1">
          <label for="f-bp">birth place</label>
          <input id="f-bp" bind:value={draft.birthPlace} on:blur={save} />
        </div>
        <div class="field" style="flex:1">
          <label for="f-dp">death place</label>
          <input id="f-dp" bind:value={draft.deathPlace} on:blur={save} />
        </div>
      </div>
      <div class="field">
        <label for="f-notes">notes</label>
        <textarea id="f-notes" bind:value={draft.notes} on:blur={save}></textarea>
      </div>

      <div class="card subtle">
        <div class="row head-row">
          <strong>Privacy</strong>
          <span class="pill {isLiving(draft) ? 'warn' : 'ok'}">
            {isLiving(draft) ? 'living' : 'deceased'}
          </span>
        </div>
        <p class="dim small">
          Living people are never published to the public corpus unless
          explicitly authorised. POPIA-aligned.
        </p>
        <div class="row">
          <label class="row small check">
            <input type="checkbox" style="width:auto"
              bind:checked={draft.livingOverride}
              on:change={() => { if (draft) { draft.living = isLiving(draft); save(); } }} />
            <span>override auto-detection</span>
          </label>
          {#if draft.livingOverride}
            <label class="row small check">
              <input type="checkbox" style="width:auto"
                bind:checked={draft.living} on:change={save} />
              <span>mark as living</span>
            </label>
          {/if}
        </div>
        {#if isLiving(draft)}
          <label class="row small check">
            <input type="checkbox" style="width:auto"
              bind:checked={draft.publishAuthorized} on:change={save} />
            <span>publish consent recorded</span>
          </label>
        {/if}
      </div>

      <div class="card subtle">
        <div class="row head-row">
          <strong>Relationships</strong>
          <span class="chip">{relsOnPerson.length}</span>
        </div>
        <div class="row">
          <button class="small" on:click={() => quickAddRelative('parent')}>+ parent</button>
          <button class="small" on:click={() => quickAddRelative('child')}>+ child</button>
          <button class="small" on:click={() => quickAddRelative('spouse')}>+ spouse</button>
        </div>

        <p class="dim small">or link to existing:</p>
        <div class="row">
          <select bind:value={linkMode}>
            <option value={null}>—</option>
            <option value="parent">add parent</option>
            <option value="child">add child</option>
            <option value="spouse">add spouse</option>
          </select>
          <select bind:value={pickTarget} disabled={!linkMode}>
            <option value="">pick person…</option>
            {#each candidates as c (c.id)}
              <option value={c.id}>{c.name}</option>
            {/each}
          </select>
          <button class="small" on:click={addLink} disabled={!linkMode || !pickTarget}>link</button>
        </div>

        <ul class="rels">
          {#each relsOnPerson as r (r.id)}
            {@const other = peopleById.get(r.a === draft.id ? r.b : r.a)}
            <li>
              <span class="chip">{r.kind === 'spouse' ? 'spouse' : (r.a === draft.id ? 'child' : 'parent')}</span>
              <button class="linklike" on:click={() => other && selectedPersonId.set(other.id)}>
                {other?.name ?? '—'}
              </button>
              <span class="chip dim small">{r.id.slice(0, 6)}</span>
              <button class="ghost small danger" on:click={() => unlink(r.id)}>✕</button>
            </li>
          {/each}
          {#if !relsOnPerson.length}<li class="dim small">no relationships yet.</li>{/if}
        </ul>
      </div>

      <div class="row">
        <button class="primary" on:click={save}>save</button>
        <button class="danger" on:click={remove}>delete</button>
      </div>
    </div>
  </aside>
{/if}

<style>
  .drawer {
    position: absolute; top: 0; right: 0; bottom: 0;
    width: var(--ls-drawer-w);
    max-width: 100%;
    background: var(--ls-surface);
    border-left: 1px solid var(--ls-border);
    overflow: auto;
    padding: 16px 16px 60px;
    box-shadow: var(--ls-shadow-lg);
    z-index: 10;
  }
  header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 10px; }
  .stack-tight { display: flex; flex-direction: column; gap: 6px; }
  h3 { margin: 0; font-size: var(--ls-step-3); }
  .card.subtle { background: var(--ls-surface-2); border: 1px solid var(--ls-border); padding: 14px; border-radius: var(--ls-radius); }
  .head-row { justify-content: space-between; margin-bottom: 4px; }
  .small { font-size: var(--ls-step-0); }
  .check { gap: 6px; color: var(--ls-text); }
  .rels { list-style: none; padding: 0; margin: 10px 0 0; }
  .rels li { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px dashed var(--ls-border); flex-wrap: wrap; }
  .rels li:last-child { border-bottom: none; }
  .linklike { background: transparent; border: none; padding: 0; color: var(--ls-signal); font-family: var(--ls-font-display); font-size: 15px; }
  .linklike:hover { background: transparent; text-decoration: underline; }
  @media (max-width: 680px) { .drawer { width: 100%; } }
</style>
